// React
import React, { useEffect, useState } from "react";

// Third-Party
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import { FileText, X, AlertCircle } from "lucide-react";

// Internal
import {
  getPdfPreviewFile,
  previewDocumentHandle,
} from "@pages/askDost/services/askDostService";
import {
  CSV_LIKE_KINDS,
  DOCUMENT_PREVIEW_EXCEL_UNSUPPORTED_MESSAGE,
  DOCUMENT_PREVIEW_UNSUPPORTED_MESSAGE,
  EXCEL_FILE_EXTENSIONS,
  IMAGE_FILE_EXTENSIONS,
} from "@pages/askDost/utils/constants";
import {
  docPreviewErrorStyle,
  docPreviewIframeStyle,
  docPreviewImageStyle,
  docPreviewImageWrapperStyle,
  docPreviewLoadingStyle,
  docPreviewTableWrapperStyle,
  docPreviewDialogTitleStyle,
  getDocPreviewCellStyle,
  getDocPreviewCloseButtonStyle,
  getDocPreviewDialogContentStyle,
  getDocPreviewDialogPaperStyle,
  getDocPreviewHeaderCellStyle,
  getDocPreviewRowStyle,
  getDocPreviewSheetHeaderCellStyle,
  getDocPreviewTableStyle,
  getDocPreviewTitleTextStyle,
} from "@pages/askDost/styles/askDostStyles";

// Types
import {
  IDocumentPreviewCsvData,
  IDocumentPreviewModalProps,
  TDocumentPreviewFileType,
} from "@pages/askDost/types";

export const DocumentPreviewModal: React.FC<IDocumentPreviewModalProps> = ({
  doc,
  onClose,
  palette,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<IDocumentPreviewCsvData | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<TDocumentPreviewFileType | null>(
    null,
  );

  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchPreview = async () => {
      if (!doc?.handle) return;

      setLoading(true);
      setError(null);
      setCsvData(null);
      setFileUrl(null);
      setFileType(null);

      try {
        const response = await previewDocumentHandle(doc?.handle);

        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status}`);
        }

        const contentType = response.headers.get("content-type") || "";
        const fileNameLower = (doc?.name || "").toLowerCase();

        if (contentType.includes("application/json")) {
          const data = await response.json();

          if (CSV_LIKE_KINDS.includes(data.kind) && data.text) {
            const lines = data.text
              .split("\n")
              .filter((r: string) => r.trim() !== "");

            const parsedRows = lines.map((line: string) => {
              const row: string[] = [];
              let cur = "";
              let inQuote = false;
              for (let i = 0; i < line.length; i++) {
                if (line[i] === '"') {
                  inQuote = !inQuote;
                } else if (line[i] === "," && !inQuote) {
                  row.push(cur);
                  cur = "";
                } else {
                  cur += line[i];
                }
              }
              row.push(cur);
              return row;
            });

            const hasSheets = parsedRows.some(
              (r) => r?.[0] && r?.[0]?.startsWith("# Sheet:"),
            );

            if (data.kind === "csv" && !hasSheets && parsedRows.length > 0) {
              setCsvData({ headers: parsedRows[0], rows: parsedRows.slice(1) });
            } else {
              setCsvData({ headers: [], rows: parsedRows });
            }
          }
          // PDF Handler
          else if (data.kind === "pdf") {
            const pdfResponse = await getPdfPreviewFile(doc.handle);
            if (!pdfResponse.ok) {
              throw new Error(
                `Failed to load PDF preview: ${pdfResponse.status}`,
              );
            }
            const blob = await pdfResponse.blob();
            objectUrl = window.URL.createObjectURL(blob);
            setFileUrl(objectUrl);
            setFileType("pdf");
          }
          // Image URL Handler
          else if (data.kind === "image" && (data.url || data.previewUrl)) {
            setFileUrl(data.url || data.previewUrl);
            setFileType("image");
          } else if (data.url || data.previewUrl || data.preview_url) {
            setFileUrl(data.url || data.previewUrl || data.preview_url);
            setFileType("other");
          } else {
            setError(DOCUMENT_PREVIEW_UNSUPPORTED_MESSAGE);
          }
        }
        // Direct Image Blob
        else if (
          contentType.includes("image/") ||
          IMAGE_FILE_EXTENSIONS.some((ext) => fileNameLower.endsWith(ext))
        ) {
          const blob = await response.blob();
          objectUrl = window.URL.createObjectURL(blob);
          setFileUrl(objectUrl);
          setFileType("image");
        } else if (
          contentType.includes("application/pdf") ||
          fileNameLower.endsWith(".pdf")
        ) {
          const blob = await response.blob();
          objectUrl = window.URL.createObjectURL(blob);
          setFileUrl(objectUrl);
          setFileType("pdf");
        } else if (
          EXCEL_FILE_EXTENSIONS.some((ext) => fileNameLower.endsWith(ext))
        ) {
          setError(DOCUMENT_PREVIEW_EXCEL_UNSUPPORTED_MESSAGE);
        } else {
          const blob = await response.blob();
          objectUrl = window.URL.createObjectURL(blob);
          setFileUrl(objectUrl);
          setFileType("other");
        }
      } catch (err) {
        console.error("Error fetching document:", err);
        setError(DOCUMENT_PREVIEW_UNSUPPORTED_MESSAGE);
      } finally {
        setLoading(false);
      }
    };

    if (doc) {
      if (doc?.url && !doc?.handle) {
        setFileUrl(doc?.url);
        setFileType(
          doc?.url?.match(/\.(jpeg|jpg|gif|png)$/) != null ? "image" : "other",
        );
      } else {
        fetchPreview();
      }
    }

    return () => {
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [doc]);

  const renderLoadingState = () => (
    <Box sx={docPreviewLoadingStyle}>
      <CircularProgress color="primary" />
    </Box>
  );

  const renderErrorState = () => (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="center"
      alignItems="center"
      sx={docPreviewErrorStyle}
    >
      <AlertCircle color={palette.error.main} />
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    </Stack>
  );

  const renderImagePreview = () => (
    <Box sx={docPreviewImageWrapperStyle}>
      <img
        src={fileUrl || undefined}
        alt={doc?.name}
        style={docPreviewImageStyle}
      />
    </Box>
  );

  const renderPdfOrOtherPreview = () => (
    <iframe
      src={fileUrl || undefined}
      width="100%"
      height="100%"
      style={docPreviewIframeStyle}
      title={doc?.name || "Document"}
    />
  );

  const renderCSVTable = () => {
    if (!csvData) return null;

    const isExcel = csvData.headers.length === 0;

    return (
      <Box sx={docPreviewTableWrapperStyle}>
        <Table sx={getDocPreviewTableStyle(palette, isExcel)}>
          {!isExcel && (
            <TableHead>
              <TableRow>
                {csvData.headers.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={getDocPreviewHeaderCellStyle(palette)}
                  >
                    {header.trim()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {csvData.rows.map((row, rowIndex) => {
              const isSheetHeader =
                row?.[0] && String(row?.[0]).startsWith("# Sheet:");

              if (isSheetHeader) {
                return (
                  <TableRow key={rowIndex} sx={{ p: 1.5 }}>
                    <TableCell
                      colSpan={100}
                      sx={getDocPreviewSheetHeaderCellStyle(palette)}
                    >
                      {String(row?.[0]).replace("# Sheet:", "SHEET:").trim()}
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow
                  key={rowIndex}
                  sx={getDocPreviewRowStyle(palette, isExcel)}
                >
                  {row.map((cell, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      sx={getDocPreviewCellStyle(palette, isExcel)}
                    >
                      {cell.trim()}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Dialog
      open={Boolean(doc)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: getDocPreviewDialogPaperStyle(Boolean(csvData)) }}
    >
      <DialogTitle sx={docPreviewDialogTitleStyle}>
        <FileText size={16} color={palette.primary.main} />
        <Typography
          variant="subtitle2"
          noWrap
          sx={getDocPreviewTitleTextStyle(palette)}
        >
          {doc?.name || doc?.handle || "Document Preview"}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={getDocPreviewCloseButtonStyle(palette)}
        >
          <X size={16} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={getDocPreviewDialogContentStyle(palette, fileType)}>
        {loading && renderLoadingState()}

        {!loading && error && renderErrorState()}

        {!loading && !error && csvData && renderCSVTable()}

        {!loading &&
          !error &&
          fileUrl &&
          fileType === "image" &&
          renderImagePreview()}

        {!loading &&
          !error &&
          fileUrl &&
          (fileType === "pdf" || fileType === "other") &&
          renderPdfOrOtherPreview()}
      </DialogContent>
    </Dialog>
  );
};
